import {useState} from "react";
import {ExternalLinkItem} from "../types/links";
import {openSuccessNotification, showErrorNotification} from "../helpers/mainHelpers";
import {externalLinksService} from "../services/externalLinks-service";
import {useForm} from "antd/es/form/Form";
import {Modal} from "antd";
import {DeleteOutlined} from "@ant-design/icons";

export const useExternalLinksController = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [lastUpdate, setLastUpdate] = useState(0)
  const [externalLinksList, setExternalLinksList] = useState<ExternalLinkItem[]>([])
  const [selectedExternalLink, setSelectedExternalLink] = useState<ExternalLinkItem | undefined>(undefined)
  const [form] = useForm()

  const toggleModal = () => {
    const newValue = !isModalOpen
    setIsModalOpen(newValue)
    if (newValue === false) {
      form.resetFields()
      setSelectedExternalLink(undefined)
    }
  }

  const toggleLastUpdate = () => {
    setLastUpdate(Date.now())
  }

  const fetchExternalLinksList = async (queries: string[]) => {
    setIsLoading(true)
    try {
      const list = await externalLinksService.list(queries)
      setExternalLinksList(list)
    } catch (error) {
      console.error(error)
      showErrorNotification({message: "Помилка завантаження посилань!"});
    } finally {
      setIsLoading(false)
    }
  }

  const createExternalLinkItem = async (data: ExternalLinkItem) => {
    setIsLoading(true)
    try {
      await externalLinksService.create(data)
      openSuccessNotification("Посилання на ресурс успішно додано!")
      toggleModal()
      toggleLastUpdate()
    } catch (error) {
      showErrorNotification({
        message: "Помилка при додаванні посилання на ресурс!",
        description: JSON.stringify(error)
      })
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  const updateExternalLinkItem = async (data: ExternalLinkItem) => {
    setIsLoading(true)
    try {
      await externalLinksService.update({$id: selectedExternalLink?.$id, ...data})
      toggleModal()
      toggleLastUpdate()
      openSuccessNotification("Посилання на ресурс успішно оновлено!")
    } catch (error) {
      console.error(error)
      showErrorNotification({
        message: "Помилка при оновленні посилання на ресурс!",
        description: JSON.stringify(error)
      })
    } finally {
      setIsLoading(false)
    }
  }


  const confirmDelete = (props: { id: string }) => {
    const {confirm} = Modal;
    confirm(
      {
        width: 400,
        icon: <DeleteOutlined/>,
        title: `Видалити запис?`,
        okText: "Видалити",
        okButtonProps: {danger: true},
        cancelText: "Cкасувати",
        onOk: async () => {
          await deleteExternalLinkItem(props.id)
        },
      });
  }

  const deleteExternalLinkItem = async (id: string) => {
    setIsLoading(true)
    try {
      await externalLinksService.delete(id)
      const updatedList = externalLinksList.filter(item => item.$id !== id)
      setExternalLinksList(updatedList)
      openSuccessNotification("Запис успішно видалено!")
    } catch (error) {
      console.error(error)
      showErrorNotification({message: "Помилка видалення запису!", description: JSON.stringify(error)});
    } finally {
      setIsLoading(false)
    }
  }

  const submitButtonHandler = async (formData: ExternalLinkItem) => {
    if (selectedExternalLink) {
      await updateExternalLinkItem(formData)
    } else {
      await createExternalLinkItem(formData)
    }
  }

  const editButtonHandler = (selectedItem: ExternalLinkItem) => {
    setSelectedExternalLink(selectedItem)
    toggleModal()
  }

  return {
    fetchExternalLinksList,
    externalLinksList,
    toggleModal,
    selectedExternalLink,
    isLoading,
    form,
    isModalOpen,
    submitButtonHandler,
    lastUpdate,
    confirmDelete,
    editButtonHandler
  }
}